import React, { useState, useEffect, ReactNode, useRef } from 'react';
import { LoadingState } from '@/shared/components';

interface AdvancedPageTransitionProps {
  children: ReactNode;
}

const AdvancedPageTransition: React.FC<AdvancedPageTransitionProps> = ({
  children,
}) => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentPage, setCurrentPage] = useState(children);
  const [nextPage, setNextPage] = useState<ReactNode>(null);
  const [transitionPhase, setTransitionPhase] = useState<
    'idle' | 'loading' | 'transitioning'
  >('idle');
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (children !== currentPage) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Start transition
      setTransitionPhase('loading');
      setNextPage(children);
      setIsTransitioning(true);

      // Minimum loading time for smooth UX
      timeoutRef.current = setTimeout(() => {
        setTransitionPhase('transitioning');

        // Brief transition phase
        setTimeout(() => {
          setCurrentPage(children);
          setNextPage(null);
          setIsTransitioning(false);
          setTransitionPhase('idle');
        }, 200);
      }, 300);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
  }, [children, currentPage]);

  return (
    <div className='relative min-h-screen overflow-hidden'>
      {/* Current page with transition effects */}
      <div
        className={`transition-all duration-500 ease-in-out ${
          isTransitioning
            ? 'opacity-30 scale-95 blur-sm transform -translate-y-2'
            : 'opacity-100 scale-100 blur-0 transform translate-y-0'
        }`}
        style={{
          filter: isTransitioning ? 'brightness(0.7)' : 'brightness(1)',
        }}
      >
        {currentPage}
      </div>

      {/* Loading overlay with backdrop blur */}
      {isTransitioning && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
            transitionPhase === 'loading'
              ? 'bg-[#F1F1E9] bg-opacity-95 backdrop-blur-md'
              : 'bg-transparent backdrop-blur-none'
          }`}
        >
          <div
            className={`transition-all duration-300 ${
              transitionPhase === 'loading'
                ? 'opacity-100 scale-100'
                : 'opacity-0 scale-95'
            }`}
          >
            <LoadingState size='lg' text='Loading The Sanur...' />
          </div>
        </div>
      )}

      {/* Next page preview (subtle) */}
      {nextPage && transitionPhase === 'transitioning' && (
        <div
          className='fixed inset-0 z-40 opacity-0 pointer-events-none'
          style={{
            background:
              'linear-gradient(135deg, rgba(241, 241, 233, 0.1) 0%, rgba(193, 142, 16, 0.05) 100%)',
          }}
        >
          {nextPage}
        </div>
      )}

      {/* Progress indicator */}
      {isTransitioning && (
        <div className='fixed top-0 left-0 right-0 h-1 bg-gray-200 z-50'>
          <div
            className='h-full bg-gradient-to-r from-[#C18E10] to-[#A67A0E] transition-all duration-300 ease-out'
            style={{
              width: transitionPhase === 'loading' ? '70%' : '100%',
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AdvancedPageTransition;
